import * as React from 'react';

import type {
  AudienceDelta,
  CohortAudience,
  Learner,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import type { LearnerListAudience } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearners';
import {
  AUDIENCE_LEARNERS_PAGE_SIZE,
  useAudienceLearners,
} from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearners';
import { useCohortLearnerPaging } from 'bundles/enterprise-admin-messaging-v2/hooks/useCohortLearnerPaging';
import { useCohortMembershipCheck } from 'bundles/enterprise-admin-messaging-v2/hooks/useCohortMembershipCheck';
import { LEARNER_LIST_PAGE_SIZE } from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';
import type { LearnerRow } from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';
import type { UserFilter } from 'bundles/enterprise-admin-user-filters/types';

const SEARCH_DEBOUNCE_MS = 300;

type Params = {
  cohortAudience?: CohortAudience;
  /** Delta to seed the editor with when re-opening. */
  initialDelta?: AudienceDelta;
  /** Learner objects for `initialDelta.addedIds`, to seed the map on re-open. */
  initialAddedLearners?: Learner[];
};

type Result = {
  searchQuery: string;
  setSearchQuery: (next: string) => void;
  page: number;
  setPage: (next: number) => void;
  pageCount: number;
  pageRows: LearnerRow[];
  filteredCount: number;
  loading: boolean;
  error: boolean;
  addLearner: (learner: Learner) => void;
  removeLearner: (id: string) => void;
  delta: AudienceDelta;
  /** Full Learner objects for learners in `delta.addedIds`. */
  addedLearners: Learner[];
};

/**
 * Builds an org-scoped MULTI_TYPE_FILTER that fetches both INVITED and JOINED
 * learners — the full pool from which the admin can add/remove for the cohort.
 */
const buildOrgLearnerFilter = (): UserFilter => ({
  primaryFilter: {
    type: 'MULTI_TYPE_FILTER',
    multiFilter: {
      membershipStates: ['INVITED', 'JOINED'],
    },
  },
});

/**
 * Editor hook for the AI cohort (dynamic cohort resolution) flow's "Add or
 * remove learners" screen.
 *
 * **Default view (no search):** Paginates the cohort members directly via
 * `fetchEnterpriseLearnerCohorts`. Every row is a natural cohort member and
 * shows a delete icon (unless the admin has already removed it via the delta).
 *
 * **Search view:** Searches the org-wide pool (INVITED + JOINED via
 * `queryByFilter`) and for each page, checks cohort membership in parallel.
 * Learners in the cohort show a delete icon; learners outside show "+ Add".
 *
 * Edits are tracked as a bidirectional delta (`addedIds` + `removedIds`) and
 * override membership in both views.
 *
 * The hook is skipped (no-op) when `cohortAudience` is falsy.
 */
export const useCohortLearnerEditor = ({ cohortAudience, initialDelta, initialAddedLearners }: Params): Result => {
  const [addedIds, setAddedIds] = React.useState<Set<string>>(() => new Set(initialDelta?.addedIds));
  const [removedIds, setRemovedIds] = React.useState<Set<string>>(() => new Set(initialDelta?.removedIds));
  const [addedLearnerMap, setAddedLearnerMap] = React.useState<Map<string, Learner>>(
    () => new Map(initialAddedLearners?.map((l) => [l.id, l]))
  );

  // --- Search state (owned here so both data sources share it) ---
  const [searchQuery, setSearchQueryRaw] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [page, setPageRaw] = React.useState(1);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const isSearching = debouncedSearch.length > 0;

  const setSearchQuery = React.useCallback((next: string) => {
    setSearchQueryRaw(next);
    setPageRaw(1);
  }, []);

  const setPage = React.useCallback((next: number) => {
    setPageRaw(next);
  }, []);

  // =======================================================================
  // DATA SOURCE 1: Cohort pagination (default view, no search)
  // =======================================================================
  const {
    learners: cohortLearners,
    total: cohortTotal,
    loading: cohortLoading,
    error: cohortError,
  } = useCohortLearnerPaging({
    cohortAudience,
    page,
    skip: isSearching,
  });

  // =======================================================================
  // DATA SOURCE 2: Org pool (INVITED + JOINED) for search view.
  // Uses useAudienceLearners directly with our managed page/search state.
  // =======================================================================
  const orgAudience: LearnerListAudience | undefined = React.useMemo(() => {
    if (!cohortAudience || !isSearching) return undefined;
    return { orgId: cohortAudience.orgId, userFilter: buildOrgLearnerFilter() };
  }, [cohortAudience, isSearching]);

  const {
    learners: poolLearners,
    total: poolTotal,
    loading: poolLoading,
    error: poolError,
  } = useAudienceLearners({
    audience: orgAudience,
    page,
    search: debouncedSearch,
  });

  // For the search view: check cohort membership for the current page's learners.
  const emailsForCheck = React.useMemo(
    () => (isSearching ? poolLearners.map((l) => l.email) : []),
    [isSearching, poolLearners]
  );
  const {
    memberEmails,
    loading: membershipLoading,
    error: membershipError,
  } = useCohortMembershipCheck({
    cohortAudience,
    emails: emailsForCheck,
  });

  // =======================================================================
  // Derive page rows — the unified output regardless of data source
  // =======================================================================
  const pageRows: LearnerRow[] = React.useMemo(() => {
    if (!isSearching) {
      // Default view: all learners come from the cohort — they're natural members.
      return cohortLearners.map((learner) => {
        let inList: boolean;
        if (addedIds.has(learner.id)) {
          inList = true;
        } else if (removedIds.has(learner.id)) {
          inList = false;
        } else {
          inList = true;
        }
        return { learner, inList };
      });
    }

    // Search view: learners come from org pool; membership is from the check.
    return poolLearners.map((learner) => {
      const isNaturalMember = memberEmails.has(learner.email);
      let inList: boolean;
      if (addedIds.has(learner.id)) {
        inList = true;
      } else if (removedIds.has(learner.id)) {
        inList = false;
      } else {
        inList = isNaturalMember;
      }
      return { learner, inList };
    });
  }, [isSearching, cohortLearners, poolLearners, memberEmails, addedIds, removedIds]);

  // =======================================================================
  // Unified pagination
  // =======================================================================
  const total = isSearching ? poolTotal : cohortTotal;
  const poolPageCount = Math.max(1, Math.ceil(poolTotal / AUDIENCE_LEARNERS_PAGE_SIZE));
  const pageCount = isSearching ? poolPageCount : Math.max(1, Math.ceil(cohortTotal / LEARNER_LIST_PAGE_SIZE));
  const loading = isSearching ? poolLoading || membershipLoading : cohortLoading;
  const error = isSearching ? Boolean(poolError) || Boolean(membershipError) : cohortError;

  // Clamp page when total shrinks (e.g. switching from search to default).
  React.useEffect(() => {
    if (total > 0 && page > pageCount) setPageRaw(pageCount);
  }, [page, pageCount, total]);

  // =======================================================================
  // Add / Remove
  // =======================================================================
  const addLearner = React.useCallback((learner: Learner) => {
    const id = learner.id;
    setRemovedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setAddedIds((prev) => new Set(prev).add(id));
    setAddedLearnerMap((prev) => new Map(prev).set(id, learner));
  }, []);

  const removeLearner = React.useCallback((id: string) => {
    setAddedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setRemovedIds((prev) => new Set(prev).add(id));
    setAddedLearnerMap((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const delta: AudienceDelta = React.useMemo(
    () => ({ addedIds: [...addedIds], removedIds: [...removedIds] }),
    [addedIds, removedIds]
  );

  const addedLearners: Learner[] = React.useMemo(() => [...addedLearnerMap.values()], [addedLearnerMap]);

  return {
    searchQuery,
    setSearchQuery,
    page,
    setPage,
    pageCount,
    pageRows,
    filteredCount: total,
    loading,
    error,
    addLearner,
    removeLearner,
    delta,
    addedLearners,
  };
};

export default useCohortLearnerEditor;
